// Theme Configuration - All available color themes for MAANTT
// Each theme defines CSS custom properties that will be applied to :root

export type ThemeId = 'ocean-teal' | 'midnight-ember' | 'arctic-frost' | 'neon-pulse' | 'emerald-matrix' | 'maantt-brand';

export interface ThemeColors {
  // Background colors
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  
  // Accent colors
  accentPrimary: string;
  accentSecondary: string;
  accentTertiary: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  
  // UI colors
  border: string;
  borderHover: string;
  cardBg: string;
  cardBgHover: string;
  
  // Glow/shadow colors (with alpha)
  glowPrimary: string;
  glowSecondary: string;
}

export interface Theme {
  id: ThemeId;
  name: string;
  description: string;
  icon: string;
  colors: ThemeColors;
}

export const themes: Record<ThemeId, Theme> = {
  'ocean-teal': {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    description: 'Classic dark navy with teal accents',
    icon: '🌊',
    colors: {
      bgPrimary: '#0a0f1a',
      bgSecondary: '#0d1321',
      bgTertiary: '#111827',
      accentPrimary: '#00d4aa',
      accentSecondary: '#14b8a6',
      accentTertiary: '#3b82f6',
      textPrimary: '#ffffff',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      border: 'rgba(255, 255, 255, 0.1)',
      borderHover: 'rgba(0, 212, 170, 0.3)',
      cardBg: 'rgba(255, 255, 255, 0.05)',
      cardBgHover: 'rgba(255, 255, 255, 0.08)',
      glowPrimary: 'rgba(0, 212, 170, 0.4)',
      glowSecondary: 'rgba(59, 130, 246, 0.3)',
    },
  },
  
  'midnight-ember': {
    id: 'midnight-ember',
    name: 'Midnight Ember',
    description: 'Warm dark theme with coral and amber',
    icon: '🔥',
    colors: {
      bgPrimary: '#0c0c0f',
      bgSecondary: '#141418',
      bgTertiary: '#1c1c21',
      accentPrimary: '#ff6b4a',
      accentSecondary: '#f59e0b',
      accentTertiary: '#fb923c',
      textPrimary: '#fafaf9',
      textSecondary: '#a8a29e',
      textMuted: '#78716c',
      border: 'rgba(255, 255, 255, 0.08)',
      borderHover: 'rgba(255, 107, 74, 0.4)',
      cardBg: 'rgba(255, 255, 255, 0.04)',
      cardBgHover: 'rgba(255, 107, 74, 0.08)',
      glowPrimary: 'rgba(255, 107, 74, 0.4)',
      glowSecondary: 'rgba(245, 158, 11, 0.3)',
    },
  },
  
  'arctic-frost': {
    id: 'arctic-frost',
    name: 'Arctic Frost',
    description: 'Cool dark theme with ice blue accents',
    icon: '❄️',
    colors: {
      bgPrimary: '#030712',
      bgSecondary: '#0f172a',
      bgTertiary: '#1e293b',
      accentPrimary: '#22d3ee',
      accentSecondary: '#38bdf8',
      accentTertiary: '#0ea5e9',
      textPrimary: '#ffffff',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      border: 'rgba(255, 255, 255, 0.1)',
      borderHover: 'rgba(34, 211, 238, 0.4)',
      cardBg: 'rgba(255, 255, 255, 0.05)',
      cardBgHover: 'rgba(34, 211, 238, 0.08)',
      glowPrimary: 'rgba(34, 211, 238, 0.4)',
      glowSecondary: 'rgba(56, 189, 248, 0.3)',
    },
  },
  
  'neon-pulse': {
    id: 'neon-pulse',
    name: 'Neon Pulse',
    description: 'Bold dark theme with electric purple and pink',
    icon: '⚡',
    colors: {
      bgPrimary: '#09090b',
      bgSecondary: '#0d0912',
      bgTertiary: '#18181b',
      accentPrimary: '#a855f7',
      accentSecondary: '#ec4899',
      accentTertiary: '#06b6d4',
      textPrimary: '#fafafa',
      textSecondary: '#a1a1aa',
      textMuted: '#71717a',
      border: 'rgba(255, 255, 255, 0.08)',
      borderHover: 'rgba(168, 85, 247, 0.4)',
      cardBg: 'rgba(255, 255, 255, 0.04)',
      cardBgHover: 'rgba(168, 85, 247, 0.1)',
      glowPrimary: 'rgba(168, 85, 247, 0.5)',
      glowSecondary: 'rgba(236, 72, 153, 0.4)',
    },
  },
  
  'emerald-matrix': {
    id: 'emerald-matrix',
    name: 'Emerald Matrix',
    description: 'Fresh dark theme with green and mint accents',
    icon: '🌿',
    colors: {
      bgPrimary: '#0a0f0a',
      bgSecondary: '#0d1a0d',
      bgTertiary: '#14281a',
      accentPrimary: '#10b981',
      accentSecondary: '#34d399',
      accentTertiary: '#84cc16',
      textPrimary: '#f0fdf4',
      textSecondary: '#86efac',
      textMuted: '#4ade80',
      border: 'rgba(255, 255, 255, 0.08)',
      borderHover: 'rgba(16, 185, 129, 0.4)',
      cardBg: 'rgba(255, 255, 255, 0.04)',
      cardBgHover: 'rgba(16, 185, 129, 0.08)',
      glowPrimary: 'rgba(16, 185, 129, 0.4)',
      glowSecondary: 'rgba(52, 211, 153, 0.3)',
    },
  },
  
  'maantt-brand': {
    id: 'maantt-brand',
    name: 'MAANTT Brand',
    description: 'Official brand colors - blue and gold',
    icon: '🏢',
    colors: {
      bgPrimary: '#0a1628',
      bgSecondary: '#0d1e36',
      bgTertiary: '#132744',
      accentPrimary: '#F5A623',
      accentSecondary: '#0072B5',
      accentTertiary: '#0090E3',
      textPrimary: '#ffffff',
      textSecondary: '#a4c4e0',
      textMuted: '#6b8fb5',
      border: 'rgba(255, 255, 255, 0.1)',
      borderHover: 'rgba(245, 166, 35, 0.4)',
      cardBg: 'rgba(255, 255, 255, 0.05)',
      cardBgHover: 'rgba(245, 166, 35, 0.1)',
      glowPrimary: 'rgba(245, 166, 35, 0.4)',
      glowSecondary: 'rgba(0, 114, 181, 0.3)',
    },
  },
};

export const themeList = Object.values(themes);
export const defaultTheme: ThemeId = 'maantt-brand';

// Convert theme colors to CSS custom properties
export function themeToCSSVariables(theme: Theme): Record<string, string> {
  const { colors } = theme;
  return {
    '--color-bg-primary': colors.bgPrimary,
    '--color-bg-secondary': colors.bgSecondary,
    '--color-bg-tertiary': colors.bgTertiary,
    '--color-accent-primary': colors.accentPrimary,
    '--color-accent-secondary': colors.accentSecondary,
    '--color-accent-tertiary': colors.accentTertiary,
    '--color-text-primary': colors.textPrimary,
    '--color-text-secondary': colors.textSecondary,
    '--color-text-muted': colors.textMuted,
    '--color-border': colors.border,
    '--color-border-hover': colors.borderHover,
    '--color-card-bg': colors.cardBg,
    '--color-card-bg-hover': colors.cardBgHover,
    '--color-glow-primary': colors.glowPrimary,
    '--color-glow-secondary': colors.glowSecondary,
  };
}
